// React imports
import { useEffect, useMemo, useState } from "react";

// UI Components
import { Button } from "shared/ui/shadcn/button";
import { EditIcon } from "lucide-react";
import { clsxMerge } from "shared/lib/helpers";

// Local components
import { TemplatesSelect } from "@/components/modules/email-templates-sidebar/content/v2/sections/prompt-section/templates-select";
import { PromptTextarea } from "@/modules/email-templates-sidebar/content/v2/sections/prompt-section/prompt-textarea";
import { EmailPromptNameInput } from "@/modules/email-templates-sidebar/content/v2/sections/prompt-section/prompt-name-input";
import { PromptDeleteModal } from "@/modules/email-templates-sidebar/content/v2/sections/prompt-section/prompt-delete-modal";
import { GenereateAIEmailButton } from "@/modules/email-templates-sidebar/shared/generate-ai-email-button";

// Context
import { useEmailTemplatesContext } from "@/modules/email-templates-sidebar/context";
import { DEFAULT_PROMPT_NAME } from "@/components/modules/email-templates-sidebar/constants";

export const PromptSection = () => {
  const { activeTemplate, activeEmailSequenceItemId, drafts, getDraft } =
    useEmailTemplatesContext();

  const [isEditMode, setIsEditMode] = useState(false);

  const handleOnEdit = () => {
    setIsEditMode(true);
  };

  const handleOnSave = () => {
    setIsEditMode(false);
  };

  useEffect(() => {
    if (activeTemplate?.name === DEFAULT_PROMPT_NAME) {
      setIsEditMode(true);
      return;
    }

    setIsEditMode(false);
  }, [activeTemplate?.id]);

  const isFollowUp = useMemo(() => {
    const draft = getDraft(activeTemplate?.id as string);
    const emails = draft?.emails;

    const emailIndex = emails?.findIndex(
      (e) => e?.email_id === activeEmailSequenceItemId
    );

    return (emailIndex || 0) > 0;
  }, [activeTemplate, activeEmailSequenceItemId, drafts]);

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="rounded-lg bg-[#764BC70D] p-4">
        <div className="mb-3 flex items-end justify-between">
          <div className="flex items-end">
            <TemplatesSelect className="w-full max-w-[255px]" />

            {activeTemplate?.is_custom && !isEditMode && (
              <Button
                variant="link"
                size="sm"
                className="animate-fadein text-[#0F35FF] typography-body-4-medium"
                type="button"
                onClick={handleOnEdit}
              >
                <EditIcon className="h-4 w-4" /> Edit
              </Button>
            )}
          </div>
          <div>
            <GenereateAIEmailButton />
          </div>
        </div>

        {isEditMode && <EmailPromptNameInput onSave={handleOnSave} />}

        {!isFollowUp && (
          <PromptTextarea
            disabled={!activeTemplate?.is_custom}
            classNameTextarea={clsxMerge(
              "rounded-lg bg-white",
              !activeTemplate?.is_custom && "bg-[#764BC7]/10"
            )}
          />
        )}

        <PromptDeleteModal />
      </div>
    </div>
  );
};
